import { api } from './client'

export interface AICopilotResponse {
    status: string
    message: string
    lang: 'sw' | 'en'
    quick_topics: string[]
    file_info?: string
}

export interface AIVetDiagnosisResponse {
    success: boolean
    diagnosis: string
    animal_info?: string
    lang: 'sw' | 'en'
    provider?: string
}

export interface AICommodityForecast {
    name: string
    category: 'Livestock' | 'Crop' | string
    unit: string
    current_30d: number
    prev_30d: number
    daily_average: number
    projected_30d: number
    trend_pct: number
}

export interface AIFeedMetrics {
    total_feed_consumed_kg: number
    total_feed_cost: number
    fcr_ratio: number | null
    avg_daily_feed_per_animal_kg: number
    feed_efficiency_score: number
    efficiency_status: string
    concentrate_kg: number
    roughage_kg: number
    supplement_kg: number
    feeding_records_count: number
}

export interface AIFinancialMetrics {
    current_revenue: number
    operating_cost: number
    projected_revenue: number
    projected_profit: number
    projected_margin_pct: number
    daily_burn_rate: number
    daily_sales_rate: number
}

export interface AIProjectionData {
    title: string
    subtitle: string
    lang: 'sw' | 'en'
    is_data_sufficient?: boolean
    metrics: {
        current_30_production: number
        projected_30_yield: number
        yield_growth_trend: number
        primary_unit?: string
        current_revenue: number
        projected_revenue: number
        projected_profit: number
        projected_margin_pct: number
        feed_efficiency_score: number
        fcr_ratio?: number | null
        total_animals: number
        total_feed_consumed_kg?: number
        total_feed_cost?: number
        efficiency_status?: string
    }
    commodities?: AICommodityForecast[]
    feed_metrics?: AIFeedMetrics
    financial_metrics?: AIFinancialMetrics
    recommendations: string[]
}

export interface AIInventoryReorderResponse {
    success: boolean
    report: string
    items_analyzed: Array<{
        product_name: string
        category: string
        current_stock: number
        unit: string
        sales_30_days: number
        daily_burn_rate: number
        estimated_days_left: number
        risk_level: string
    }>
    lang: 'sw' | 'en'
    provider?: string
}

export interface AIExpenseAuditResponse {
    success: boolean
    report: string
    tot_expense: number
    tot_fuel: number
    flagged_count: number
    flagged_items: Array<{
        type: string
        description: string
        amount: number
        reason: string
    }>
    lang: 'sw' | 'en'
    provider?: string
}

export interface AIFeedOptimizeResponse {
    success: boolean
    formulation: string
    livestock_type: string
    animal_count: number
    lang: 'sw' | 'en'
    provider?: string
}

export interface AIWorkforceMotivationResponse {
    success: boolean
    report: string
    worker_stats: Array<{
        name: string
        department: string
        job_title: string
        completed: number
        in_progress: number
        pending: number
        total_tasks: number
        completion_rate_pct: number
    }>
    lang: 'sw' | 'en'
    provider?: string
}

export interface AIExecutiveBriefingResponse {
    success: boolean
    briefing: string
    snapshot_summary: any
    lang: 'sw' | 'en'
    provider?: string
}

export const aiService = {
    sendCopilotMessage: async (
        message: string,
        lang?: 'sw' | 'en',
        attachment?: { file?: File; image_base64?: string; filename?: string },
        history?: Array<{ role: 'user' | 'assistant'; content: string }>
    ): Promise<AICopilotResponse> => {
        if (attachment?.file) {
            const formData = new FormData()
            formData.append('message', message)
            if (lang) formData.append('lang', lang)
            formData.append('file', attachment.file)
            if (attachment.filename) formData.append('filename', attachment.filename)
            if (history && history.length > 0) formData.append('history', JSON.stringify(history))

            const response = await api.post<AICopilotResponse>('ai/copilot/chat/', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            })
            return response.data
        }

        const response = await api.post<AICopilotResponse>('ai/copilot/chat/', {
            message,
            lang,
            image_base64: attachment?.image_base64,
            filename: attachment?.filename,
            history
        })
        return response.data
    },

    getFarmProjections: async (lang: 'sw' | 'en' = 'en'): Promise<AIProjectionData> => {
        const response = await api.get<{ status: string; data: AIProjectionData }>('ai/projections/summary/', {
            params: { lang }
        })
        return response.data.data
    },

    diagnoseAnimalSymptoms: async (params: { animal_id?: string; species?: string; symptoms: string; lang?: 'sw' | 'en' }): Promise<AIVetDiagnosisResponse> => {
        const response = await api.post<{ status: string; data: AIVetDiagnosisResponse }>('ai/health/diagnose/', params)
        return response.data.data
    },

    getInventoryReorderAdvice: async (lang: 'sw' | 'en' = 'sw'): Promise<AIInventoryReorderResponse> => {
        const response = await api.get<{ status: string; data: AIInventoryReorderResponse }>('ai/inventory/reorder/', {
            params: { lang }
        })
        return response.data.data
    },

    auditExpenseAnomalies: async (lang: 'sw' | 'en' = 'sw'): Promise<AIExpenseAuditResponse> => {
        const response = await api.get<{ status: string; data: AIExpenseAuditResponse }>('ai/expenses/audit/', {
            params: { lang }
        })
        return response.data.data
    },

    optimizeFeedRation: async (params: { livestock_type: string; target_goal: string; animal_count: number; ingredients: string; lang?: 'sw' | 'en' }): Promise<AIFeedOptimizeResponse> => {
        const response = await api.post<{ status: string; data: AIFeedOptimizeResponse }>('ai/feed/optimize/', params)
        return response.data.data
    },

    evaluateWorkforceMotivation: async (lang: 'sw' | 'en' = 'sw'): Promise<AIWorkforceMotivationResponse> => {
        const response = await api.get<{ status: string; data: AIWorkforceMotivationResponse }>('ai/workforce/leaderboard_eval/', {
            params: { lang }
        })
        return response.data.data
    },

    getExecutiveBriefing: async (lang: 'sw' | 'en' = 'sw'): Promise<AIExecutiveBriefingResponse> => {
        const response = await api.get<{ status: string; data: AIExecutiveBriefingResponse }>('ai/executive/briefing/', {
            params: { lang }
        })
        return response.data.data
    }
}






